/* eslint-disable generator-star-spacing */
/* eslint space-before-function-paren: "off" */
import { toast } from 'react-toastify'
import type { SagaIterator } from 'redux-saga'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import { UpdateRequest } from '~api/types'
import { Punchlist } from '../../api'
import { EMAIL_TYPE, errorTextTryingTo, isEmpty } from '../../helpers'
import { vendorActions } from '../actions'
import { Email, Insurance, Vendor } from '../types'
import { getVendor } from './selectors'
import { AddVendorListAction, DeleteVendorInsuranceAction, FetchVendorAction, UpdateVendorAction, UpdateVendorInsuranceAction, vendorTypes } from './types'

/** Matches Contact tab: PRIMARY / BCC untouched; CC and legacy extras are editable CC slots */
const isVendorCcSlot = (e: Email) =>
  e.emailType === EMAIL_TYPE.CC ||
  (e.emailType !== EMAIL_TYPE.PRIMARY && e.emailType !== EMAIL_TYPE.BCC)

const emailForCcAddPayload = (e: Email) => ({
  email: e.email,
  emailType: EMAIL_TYPE.CC,
  name: e.name ?? '',
  description: e.description ?? '',
})

/**
 * Split next email array for JSON Patch: new CC rows (never stored on vendor) → `add`; rest → single `replace`.
 */
export const buildVendorEmailUpdateOps = (
  previous: Email[] | undefined,
  next: Email[],
): UpdateRequest => {
  const prevIds = new Set((previous ?? []).map((e) => e.id).filter(Boolean))

  const newCcEmails = next.filter(
    (e) => isVendorCcSlot(e) && (!e.id || !prevIds.has(e.id)),
  )

  if (newCcEmails.length === 0) {
    return [{ op: 'replace', path: '/email', value: next }]
  }

  const replaceEmails = next.filter((e) => !newCcEmails.includes(e))
  const ops: UpdateRequest = [{ op: 'replace', path: '/email', value: replaceEmails }]

  let insertAt = replaceEmails.findIndex((e) => e.emailType === EMAIL_TYPE.BCC)
  if (insertAt === -1)
    insertAt = replaceEmails.length

  newCcEmails.forEach((cc) => {
    ops.push({
      op: 'add',
      path: `/email/${insertAt}`,
      value: emailForCcAddPayload(cc),
    })
    insertAt += 1
  })

  return ops
}

export function* fetchVendor({ payload, callback }: FetchVendorAction): SagaIterator {
  let success = false
  try {
    // const vendor = yield call(Punchlist.vendors.getVendorById, payload)
    // const statistics = yield call(Punchlist.vendors.getVendorStatics, payload)
    // const jobs = yield call(Punchlist.vendors.getVendorJobs, payload)
    const { vendor, statistics, jobs } = yield all({
      vendor: call(Punchlist.vendors.getVendorById, payload),
      statistics: call(Punchlist.vendors.getVendorStatics, payload),
      jobs: call(Punchlist.vendors.getVendorJobs, payload)
    });
    if (vendor) {
      yield put(vendorActions.setVendor(vendor))
      yield put(vendorActions.setVendorValue({ attr: 'statistics', value: statistics }))
      yield put(vendorActions.setVendorValue({ attr: 'jobs', value: jobs }))
    }
    success = true
    if (callback) yield call(callback, success)
  } catch (error) {
    yield call(toast.error, errorTextTryingTo('get the vendor'))
    if (callback) yield call(callback, false)
  }
}

export function* updateVendor({ payload, callback }: UpdateVendorAction): SagaIterator {
  let success = false
  const vendor = yield select(getVendor())

  try {
    const request: UpdateRequest = []

    for (const attr in payload) {
      if (attr === 'email')
        continue

      request.push({
        op: 'replace',
        path: '/' + attr,
        value: payload[attr as keyof Vendor]
      })
    }

    const nextEmail = payload.email
    if (nextEmail !== undefined)
      request.push(...buildVendorEmailUpdateOps(vendor?.email, nextEmail))

    const vendorResponse = yield call(Punchlist.vendors.updateVendor, vendor.id, request)

    if (!isEmpty(vendorResponse)) {
      for (const attr in payload) {
        yield put(vendorActions.setVendorValue({ attr: attr as keyof Vendor, value: payload[attr as keyof Vendor] }))
      }
      success = true
    }
    if (callback) yield call(callback, success)
  } catch (error) {
    yield call(toast.error, errorTextTryingTo('update the vendor'))
    if (callback) yield call(callback, false)
  }
}

export function* addVendorList({ payload, list, callback }: AddVendorListAction): SagaIterator {
  let success = false
  const vendor = yield select(getVendor())
  try {
    const request: UpdateRequest = [{
      op: 'add',
      path: '/' + list + '/-',
      value: payload
    }]

    const vendorResponse = yield call(Punchlist.vendors.updateVendor, vendor.id, request)

    if (!isEmpty(vendorResponse)) {
      yield put(vendorActions.setVendorValue({ attr: list as keyof Vendor, value: vendorResponse[list] }))
      if (callback) yield call(callback, success)
    }
  } catch (error) {
    yield call(toast.error, errorTextTryingTo('update the vendor'))
    if (callback) yield call(callback, false)
  }
}

export function* updateVendorList({ payload, id, list, callback }: UpdateVendorInsuranceAction): SagaIterator {
  let success = false
  const vendor = yield select(getVendor())

  try {
    const request: UpdateRequest = []

    for (const attr in payload) {
      request.push({
        op: 'replace',
        path: `/${list}/${id}/${attr}`,
        value: payload[attr as keyof Insurance]
      })
    }
    const vendorResponse = yield call(Punchlist.vendors.updateVendor, vendor.id, request)

    if (!isEmpty(vendorResponse)) {
      const newvendor = yield call(Punchlist.vendors.getVendorById, vendor.id)
      yield put(vendorActions.setVendor(newvendor))
      success = true
    }
    if (callback) yield call(callback, success)
  } catch (error) {
    yield call(toast.error, errorTextTryingTo('get the order history'))
    if (callback) yield call(callback, false)
  }
}

export function* deleteVendorInsuranceList({ payload, id, isInsurance, callback }: DeleteVendorInsuranceAction): SagaIterator {
  let success = false
  const vendor = yield select(getVendor())

  try {
    const request: UpdateRequest = []
    if (isInsurance) {
      request.push({
        op: 'remove',
        path: `/insuranceList/${id}`
      })
      request.push({
        op: 'add',
        path: '/licenseList/-',
        value: payload
      })
    } else {
      request.push({
        op: 'remove',
        path: `/licenseList/${id}`
      })
      request.push({
        op: 'add',
        path: '/insuranceList/-',
        value: payload
      })
    }

    const vendorResponse = yield call(Punchlist.vendors.updateVendor, vendor.id, request)

    if (!isEmpty(vendorResponse)) {
      const newvendor = yield call(Punchlist.vendors.getVendorById, vendor.id)
      yield put(vendorActions.setVendor(newvendor))
      success = true
    }
    if (callback) yield call(callback, success)
  } catch (error) {
    yield call(toast.error, errorTextTryingTo('get the order history'))
    if (callback) yield call(callback, false)
  }
}

export default function* saga(): SagaIterator {
  yield takeLatest(vendorTypes.FETCH_VENDOR, fetchVendor)
  yield takeLatest(vendorTypes.UPDATE_VENDOR, updateVendor)
  yield takeLatest(vendorTypes.UPDATE_VENDOR_INSURANCE, updateVendorList)
  yield takeLatest(vendorTypes.ADD_VENDOR_LIST, addVendorList)
  yield takeLatest(vendorTypes.DELETE_VENDOR_INSURANCE, deleteVendorInsuranceList)
}
